import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";

import {
  BsArrowLeft,
  BsArrowRight,
  BsCamera,
  BsImages,
  BsTrash,
  BsXLg,
} from "react-icons/bs";

import "./jobphotossection.css";

import { uuid } from "../../utils/uuid";
import { getImageUrl } from "../../utils/helper";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const isImageFile = (file) => {
  if (file?.type?.startsWith("image/")) {
    return true;
  }

  return /\.(jpg|jpeg|png|webp|heic|heif|avif)$/i.test(file?.name || "");
};

const JobPhotosSection = ({
  images = [],
  onChange,
  disabled = false,
  lockExisting = false,
}) => {
  const imagesRef = useRef(images);

  const [enlargedImageUrl, setEnlargedImageUrl] = useState(null);
  const [enlargedImageIndex, setEnlargedImageIndex] = useState(0);

  // ==========================================================
  // OBJECT URL CLEANUP
  // ==========================================================

  useEffect(() => {
    const previousImages = imagesRef.current || [];

    const currentPreviews = new Set(
      images
        .filter((image) => image.type === "local" && image.previewUrl)
        .map((image) => image.previewUrl),
    );

    previousImages.forEach((image) => {
      if (
        image.type === "local" &&
        image.previewUrl &&
        !currentPreviews.has(image.previewUrl)
      ) {
        URL.revokeObjectURL(image.previewUrl);
      }
    });

    imagesRef.current = images;
  }, [images]);

  useEffect(() => {
    return () => {
      (imagesRef.current || []).forEach((image) => {
        if (image.type === "local" && image.previewUrl) {
          URL.revokeObjectURL(image.previewUrl);
        }
      });
    };
  }, []);

  // ==========================================================
  // FILES
  // ==========================================================

  const handleFiles = (event) => {
    const files = Array.from(event.target.files || []);

    event.target.value = "";

    if (disabled || files.length === 0) {
      return;
    }

    const validFiles = [];

    let invalidFiles = 0;
    let oversizedFiles = 0;

    files.forEach((file) => {
      if (!isImageFile(file)) {
        invalidFiles += 1;
        return;
      }

      if (file.size > MAX_IMAGE_SIZE) {
        oversizedFiles += 1;
        return;
      }

      validFiles.push(file);
    });

    if (invalidFiles > 0) {
      toast.error(
        `${invalidFiles} file${
          invalidFiles === 1 ? "" : "s"
        } skipped because they were not valid images.`,
      );
    }

    if (oversizedFiles > 0) {
      toast.error(
        `${oversizedFiles} image${
          oversizedFiles === 1 ? "" : "s"
        } skipped because the maximum size is 5MB.`,
      );
    }

    if (validFiles.length === 0) {
      return;
    }

    const newImages = validFiles.map((file) => ({
      localId: uuid(),
      type: "local",
      file,
      previewUrl: URL.createObjectURL(file),
      comment: "",
    }));

    onChange?.([...images, ...newImages]);
  };

  // ==========================================================
  // COMMENT
  // ==========================================================

  const updateComment = (localId, value) => {
    if (disabled) {
      return;
    }

    onChange?.(
      images.map((image) => {
        if (image.localId !== localId) {
          return image;
        }

        if (lockExisting && image.type === "existing") {
          return image;
        }

        return {
          ...image,
          comment: value,
        };
      }),
    );
  };

  // ==========================================================
  // REMOVE
  // ==========================================================

  const removeImage = (localId) => {
    if (disabled) {
      return;
    }

    const image = images.find((item) => item.localId === localId);

    if (!image) {
      return;
    }

    /*
     * We don't currently have a job-level
     * API for removing an existing saved photo.
     */
    if (lockExisting && image.type === "existing") {
      toast.info("Existing job photos cannot be removed from this screen yet.");

      return;
    }

    onChange?.(images.filter((item) => item.localId !== localId));
  };

  // ==========================================================
  // VIEWER
  // ==========================================================

  const viewerImages = useMemo(
    () =>
      images.map((image) => ({
        ...image,

        src:
          image.type === "local"
            ? image.previewUrl
            : getImageUrl(image.imageUrl || image.url),
      })),
    [images],
  );

  const openViewer = (localId) => {
    const index = viewerImages.findIndex((image) => image.localId === localId);

    if (index === -1) {
      return;
    }

    setEnlargedImageIndex(index);
    setEnlargedImageUrl(viewerImages[index].src);
  };

  const closeViewer = () => {
    setEnlargedImageUrl(null);
    setEnlargedImageIndex(0);
  };

  const previousImage = () => {
    if (viewerImages.length === 0) {
      closeViewer();
      return;
    }

    const nextIndex =
      enlargedImageIndex <= 0
        ? viewerImages.length - 1
        : enlargedImageIndex - 1;

    setEnlargedImageIndex(nextIndex);

    setEnlargedImageUrl(viewerImages[nextIndex].src);
  };

  const nextImage = () => {
    if (viewerImages.length === 0) {
      closeViewer();
      return;
    }

    const nextIndex =
      enlargedImageIndex >= viewerImages.length - 1
        ? 0
        : enlargedImageIndex + 1;

    setEnlargedImageIndex(nextIndex);

    setEnlargedImageUrl(viewerImages[nextIndex].src);
  };

  const currentImage = viewerImages[enlargedImageIndex] || null;

  useEffect(() => {
    if (!enlargedImageUrl) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeViewer();
      }

      if (event.key === "ArrowLeft") {
        previousImage();
      }

      if (event.key === "ArrowRight") {
        nextImage();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [enlargedImageUrl, enlargedImageIndex, viewerImages]);

  return (
    <>
      <section className="job-photos-section">
        {images.length > 0 ? (
          <div className="job-photo-grid">
            {images.map((image, index) => {
              const src =
                image.type === "local"
                  ? image.previewUrl
                  : getImageUrl(image.imageUrl || image.url);

              const existingLocked = lockExisting && image.type === "existing";

              return (
                <div key={image.localId} className="job-photo-card">
                  <div className="job-photo">
                    <button
                      type="button"
                      className="job-photo-view"
                      onClick={() => openViewer(image.localId)}
                      aria-label={`View job photo ${index + 1}`}
                    >
                      <img src={src} alt={`Job ${index + 1}`} />
                    </button>

                    {!existingLocked && (
                      <button
                        type="button"
                        className="job-photo-remove"
                        disabled={disabled}
                        onClick={() => removeImage(image.localId)}
                        aria-label="Remove job photo"
                      >
                        <BsTrash />
                      </button>
                    )}

                    <span className="job-photo-number">
                      {index + 1}
                    </span>

                    <span
                      className={`job-photo-badge ${
                        image.type === "existing" ? "existing" : "new"
                      }`}
                    >
                      {image.type === "existing" ? "Saved" : "New"}
                    </span>
                  </div>

                  <div className="job-photo-comment">
                    <label htmlFor={`job-job-photo-comment-${image.localId}`}>
                      Photo Comment
                    </label>

                    <textarea
                      id={`job-job-photo-comment-${image.localId}`}
                      rows="2"
                      value={image.comment || ""}
                      disabled={disabled || existingLocked}
                      placeholder="Optional comment for this photo..."
                      onChange={(event) =>
                        updateComment(image.localId, event.target.value)
                      }
                    />

                    {existingLocked && (
                      <small className="job-photo-saved-note">
                        Saved photo
                      </small>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="job-photo-empty">
            <BsImages />

            <strong>No job photos</strong>

            <span>
              Add photos showing requirements for job.
            </span>
          </div>
        )}

        <div className="job-photo-actions">
          <label
            htmlFor="job-job-camera"
            className={`job-photo-btn ${disabled ? "disabled" : ""}`}
          >
            <BsCamera />
            Take Photo
          </label>

          <input
            id="job-job-camera"
            type="file"
            accept="image/*"
            capture="environment"
            className="job-photo-input"
            disabled={disabled}
            onChange={handleFiles}
          />

          <label
            htmlFor="job-job-gallery"
            className={`job-photo-btn ${disabled ? "disabled" : ""}`}
          >
            <BsImages />
            Choose Photos
          </label>

          <input
            id="job-job-gallery"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/avif"
            multiple
            className="job-photo-input"
            disabled={disabled}
            onChange={handleFiles}
          />
        </div>
      </section>

      {enlargedImageUrl &&
        createPortal(
          <div
            className="job-image-viewer"
            role="dialog"
            aria-modal="true"
            aria-label="Job photo"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closeViewer();
              }
            }}
          >
            <div className="job-image-viewer-panel">
              <div className="job-image-viewer-header">
                <div>
                  <h3>Job Photo</h3>

                  <span>
                    Photo {enlargedImageIndex + 1} of {viewerImages.length}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={closeViewer}
                  aria-label="Close image viewer"
                >
                  <BsXLg />
                </button>
              </div>

              <div className="job-image-viewer-content">
                {viewerImages.length > 1 && (
                  <button
                    type="button"
                    className="previous"
                    onClick={previousImage}
                    aria-label="Previous image"
                  >
                    <BsArrowLeft />
                  </button>
                )}

                <img
                  src={enlargedImageUrl}
                  alt={`Job photo ${enlargedImageIndex + 1}`}
                />

                {viewerImages.length > 1 && (
                  <button
                    type="button"
                    className="next"
                    onClick={nextImage}
                    aria-label="Next image"
                  >
                    <BsArrowRight />
                  </button>
                )}
              </div>

              <div className="job-image-viewer-footer">
                <div>
                  <strong>Job Photo {enlargedImageIndex + 1}</strong>

                  <span>
                    {currentImage?.comment?.trim() || "No photo comment"}
                  </span>
                </div>

                <span>
                  {enlargedImageIndex + 1} / {viewerImages.length}
                </span>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default JobPhotosSection;
